import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Moon, Loader2, Play, Pause, SkipBack, SkipForward, X, ArrowUp, ArrowDown, ListMusic, Sparkles, Plus, Pencil, Trash2, Check, Ear } from "lucide-react";
import axios from "axios";
import StarCanvas from "@/components/StarCanvas";
import SleepTimer from "@/components/SleepTimer";
import AmbientSounds from "@/components/AmbientSounds";
import MiniPlayerBar from "@/components/MiniPlayerBar";
import useMediaSession, { updateMediaSessionPosition } from "@/hooks/useMediaSession";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VOICES = [
  { id: "shimmer", name: "Luna" },
  { id: "nova", name: "Aria" },
  { id: "fable", name: "Jasper" },
  { id: "onyx", name: "Barnaby" },
];

const BEDTIME_OPTIONS = [null, 1, 2, 3];

const formatTime = (s) => {
  if (!s || !isFinite(s)) return "0:00";
  const m = Math.floor(s / 60);
  return `${m}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
};

export default function PlaylistPlayer() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [user, setUser] = useState(null);
  const [playlists, setPlaylists] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [voice, setVoice] = useState("shimmer");
  const [phase, setPhase] = useState("idle"); // idle | loading | ready | error | complete
  const [audioId, setAudioId] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState("");
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [plError, setPlError] = useState("");
  const [bedtimeLimit, setBedtimeLimit] = useState(null);
  const [storiesRemaining, setStoriesRemaining] = useState(null);
  const audioRef = useRef(null);
  const jobTokenRef = useRef(0);
  const autoplayRef = useRef(false);
  const previewAudioRef = useRef(null);
  const playerCardRef = useRef(null);
  const playerObserverRef = useRef(null);
  const [playerInView, setPlayerInView] = useState(true);
  const [previewVoiceId, setPreviewVoiceId] = useState(null);
  const [previewAudioId, setPreviewAudioId] = useState(null);
  const [previewLoadingId, setPreviewLoadingId] = useState(null);
  const [previewPlayingId, setPreviewPlayingId] = useState(null);
  const [previewError, setPreviewError] = useState("");

  // Callback ref: the player card only mounts after playlist/story fetches
  // resolve, so a mount-only effect would read a still-null ref. Attaching
  // the observer from the ref callback itself guarantees it fires once the
  // node actually exists (and re-attaches if the card unmounts/remounts).
  const setPlayerCardRef = useCallback((el) => {
    playerCardRef.current = el;
    playerObserverRef.current?.disconnect();
    if (!el) return;
    playerObserverRef.current = new IntersectionObserver(
      ([entry]) => setPlayerInView(entry.isIntersecting),
      { threshold: 0, rootMargin: "-96px 0px 0px 0px" }
    );
    playerObserverRef.current.observe(el);
  }, []);

  const handlePreview = async (e, voiceId) => {
    e.stopPropagation();
    if (previewPlayingId === voiceId) {
      previewAudioRef.current?.pause();
      return;
    }
    audioRef.current?.pause();
    if (previewVoiceId === voiceId && previewAudioId) {
      const audio = previewAudioRef.current;
      if (audio) { audio.currentTime = 0; audio.play().catch(() => {}); }
      return;
    }
    setPreviewLoadingId(voiceId);
    try {
      const { data } = await axios.get(`${API}/voices/preview/${voiceId}`, { withCredentials: true });
      setPreviewVoiceId(voiceId);
      setPreviewAudioId(data.audio_id);
    } catch {
      setPreviewError("Couldn't load that voice sample — please try again.");
      setTimeout(() => setPreviewError(""), 4000);
    } finally {
      setPreviewLoadingId(null);
    }
  };

  useEffect(() => {
    if (previewAudioId && previewAudioRef.current) {
      previewAudioRef.current.play().catch(() => {});
    }
  }, [previewAudioId]);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/auth/me`, { withCredentials: true }).catch(() => null),
      axios.get(`${API}/playlists`, { withCredentials: true }).catch(() => null),
    ]).then(([me, pl]) => {
      setUser(me ? me.data : false);
      if (pl) {
        setPlaylists(pl.data);
        const fromUrl = params.get("pl");
        const initial = pl.data.find((p) => p.playlist_id === fromUrl) || pl.data[0];
        if (initial) setSelectedId(initial.playlist_id);
      }
      setLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load selected playlist's stories
  useEffect(() => {
    if (!selectedId) { setStories([]); return; }
    jobTokenRef.current++;
    setPhase("idle");
    setAudioId(null);
    setIsPlaying(false);
    setCurrentIdx(0);
    setStoriesRemaining(bedtimeLimit);
    axios.get(`${API}/playlists/${selectedId}`, { withCredentials: true })
      .then((res) => setStories(res.data.stories))
      .catch(() => setStories([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  const requestNarration = useCallback(async (storyId, voiceId, waitForIt) => {
    const { data } = await axios.post(
      `${API}/stories/${storyId}/narration`,
      { voice_id: voiceId },
      { withCredentials: true }
    );
    if (data.status === "ready" || !waitForIt) return data;
    const started = Date.now();
    while (Date.now() - started < 300000) {
      await new Promise((r) => setTimeout(r, 4000));
      const { data: st } = await axios.get(
        `${API}/stories/${storyId}/narration/status`,
        { params: { voice_id: voiceId }, withCredentials: true }
      );
      if (st.status === "ready") return st;
      if (st.status === "failed") throw new Error("narration failed");
    }
    throw new Error("narration timeout");
  }, []);

  const loadStory = useCallback(async (idx, autoplay, voiceOverride) => {
    const story = stories[idx];
    if (!story) return;
    const useVoice = voiceOverride || voice;
    const token = ++jobTokenRef.current;
    autoplayRef.current = autoplay;
    setCurrentIdx(idx);
    setPhase("loading");
    setAudioId(null);
    setIsPlaying(false);
    setProgress(0);
    setCurrentTime(0);
    setError("");
    try {
      const result = await requestNarration(story.story_id, useVoice, true);
      if (jobTokenRef.current !== token) return;
      setAudioId(result.audio_id);
      setPhase("ready");
      const next = stories[idx + 1];
      if (next) requestNarration(next.story_id, useVoice, false).catch(() => {});
    } catch {
      if (jobTokenRef.current !== token) return;
      setPhase("error");
      setError("Could not prepare narration for this story.");
    }
  }, [stories, voice, requestNarration]);

  const handleEnded = () => {
    localStorage.removeItem(`dw-resume-${audioId}`);
    if (bedtimeLimit != null) {
      const remaining = storiesRemaining - 1;
      if (remaining <= 0) {
        setStoriesRemaining(0);
        setIsPlaying(false);
        setPhase("lightsout");
        return;
      }
      setStoriesRemaining(remaining);
    }
    if (currentIdx + 1 < stories.length) {
      loadStory(currentIdx + 1, true);
    } else {
      setPhase("complete");
      setIsPlaying(false);
    }
  };

  const savePlaylistStories = async (nextStories) => {
    setStories(nextStories);
    const ids = nextStories.map((s) => s.story_id);
    setPlaylists((pls) => pls.map((p) => (p.playlist_id === selectedId ? { ...p, story_ids: ids } : p)));
    await axios.put(`${API}/playlists/${selectedId}`, { story_ids: ids }, { withCredentials: true }).catch(() => {});
  };

  const removeFromQueue = async (storyId) => {
    const next = stories.filter((s) => s.story_id !== storyId);
    if (currentIdx >= next.length) setCurrentIdx(Math.max(0, next.length - 1));
    await savePlaylistStories(next);
  };

  const moveStory = async (idx, dir) => {
    const next = [...stories];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    await savePlaylistStories(next);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setPlError("");
    try {
      const { data } = await axios.post(`${API}/playlists`, { name: newName.trim() }, { withCredentials: true });
      setPlaylists((p) => [...p, data]);
      setSelectedId(data.playlist_id);
      setNewName("");
      setCreating(false);
    } catch (err) {
      setPlError(err.response?.data?.detail || "Could not create playlist");
    }
  };

  const handleRename = async (e) => {
    e.preventDefault();
    if (!renameValue.trim()) return;
    setPlError("");
    try {
      const { data } = await axios.put(`${API}/playlists/${renamingId}`, { name: renameValue.trim() }, { withCredentials: true });
      setPlaylists((pls) => pls.map((p) => (p.playlist_id === data.playlist_id ? { ...p, name: data.name } : p)));
      setRenamingId(null);
    } catch (err) {
      setPlError(err.response?.data?.detail || "Could not rename playlist");
    }
  };

  const handleDelete = async (pl) => {
    if (!window.confirm(`Delete playlist "${pl.name}"? Your stories stay safe in the library.`)) return;
    setPlError("");
    try {
      await axios.delete(`${API}/playlists/${pl.playlist_id}`, { withCredentials: true });
      const next = playlists.filter((p) => p.playlist_id !== pl.playlist_id);
      setPlaylists(next);
      if (selectedId === pl.playlist_id) setSelectedId(next[0]?.playlist_id || null);
    } catch {
      setPlError("Could not delete playlist");
    }
  };

  const current = stories[currentIdx];
  const selected = playlists.find((p) => p.playlist_id === selectedId);

  useMediaSession({
    title: phase === "ready" ? current?.title : null,
    artist: `${selected?.name || "Playlist"} \u00b7 DreamWeaver`,
    isPlaying,
    onPlayPause: () => { const a = audioRef.current; if (a) { if (isPlaying) { a.pause(); } else { previewAudioRef.current?.pause(); a.play(); } } },
    onSeekBackward: () => { if (audioRef.current) audioRef.current.currentTime = Math.max(0, audioRef.current.currentTime - 15); },
    onSeekForward: () => { if (audioRef.current) audioRef.current.currentTime = Math.min(duration, audioRef.current.currentTime + 15); },
    onPreviousTrack: currentIdx > 0 ? () => loadStory(currentIdx - 1, true) : null,
    onNextTrack: currentIdx + 1 < stories.length ? () => loadStory(currentIdx + 1, true) : null,
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0B091A]">
        <Loader2 size={32} className="animate-spin text-amber-400" />
      </div>
    );
  }

  if (user === false) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0B091A] px-4">
        <StarCanvas />
        <div className="relative z-10 glass-card rounded-3xl p-12 max-w-md w-full text-center">
          <ListMusic size={36} className="text-purple-400 mx-auto mb-5" />
          <h2 className="font-serif text-2xl font-bold text-white mb-2">Sign in for your playlists</h2>
          <button onClick={() => navigate("/")} className="mt-4 px-8 py-3 rounded-full bg-gradient-to-r from-purple-600 to-amber-500 text-white font-semibold text-sm">
            Go to DreamWeaver
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
    <div data-testid="playlist-page" className="relative min-h-screen bg-[#0B091A] overflow-x-clip">
      <StarCanvas />
      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-8 pb-24">
        <header className="flex items-center justify-between py-6">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-600 to-amber-500 flex items-center justify-center shadow-lg shadow-purple-500/30">
              <Moon size={14} className="text-white" />
            </div>
            <span className="font-serif font-bold text-lg tracking-tight gradient-text">DreamWeaver</span>
          </Link>
          <Link to="/my-stories" className="text-sm text-slate-300 hover:text-amber-300 transition-colors">
            Library
          </Link>
        </header>

        <div className="mt-8 mb-8">
          <span className="text-xs font-mono tracking-[0.2em] uppercase text-amber-300/80">Bedtime Playlists</span>
          <h1 className="font-serif text-4xl sm:text-5xl font-bold text-white mt-3">
            One continuous <span className="gradient-text">wind-down</span>
          </h1>
        </div>

        {/* Playlist selector */}
        <div data-testid="playlist-selector" className="flex flex-wrap items-center gap-2 mb-8">
          {playlists.map((pl) =>
            renamingId === pl.playlist_id ? (
              <form key={pl.playlist_id} onSubmit={handleRename} className="flex items-center gap-1">
                <input
                  data-testid="playlist-rename-input"
                  autoFocus
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  className="px-3 py-1.5 rounded-full bg-white/5 border border-amber-400/40 text-white text-xs w-40 focus:outline-none"
                />
                <button type="submit" data-testid="playlist-rename-save" className="p-1.5 text-emerald-400"><Check size={13} /></button>
                <button type="button" onClick={() => setRenamingId(null)} className="p-1.5 text-slate-500"><X size={13} /></button>
              </form>
            ) : (
              <div
                key={pl.playlist_id}
                data-testid={`playlist-tab-${pl.playlist_id}`}
                className={`group flex items-center gap-1 pl-4 pr-2 py-1.5 rounded-full text-xs font-medium cursor-pointer transition-colors ${
                  selectedId === pl.playlist_id
                    ? "bg-amber-500/20 border border-amber-400/40 text-amber-300"
                    : "border border-purple-500/20 text-slate-400 hover:text-white"
                }`}
                onClick={() => setSelectedId(pl.playlist_id)}
              >
                {pl.name} · {pl.story_ids.length}
                <button
                  data-testid={`playlist-rename-btn-${pl.playlist_id}`}
                  onClick={(e) => { e.stopPropagation(); setRenamingId(pl.playlist_id); setRenameValue(pl.name); }}
                  className="p-2 sm:p-1 min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 flex items-center justify-center text-slate-500 hover:text-amber-300 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"
                >
                  <Pencil size={11} />
                </button>
                <button
                  data-testid={`playlist-delete-btn-${pl.playlist_id}`}
                  onClick={(e) => { e.stopPropagation(); handleDelete(pl); }}
                  className="p-2 sm:p-1 min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 flex items-center justify-center text-slate-500 hover:text-red-400 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"
                >
                  <Trash2 size={11} />
                </button>
              </div>
            )
          )}
          {creating ? (
            <form onSubmit={handleCreate} className="flex items-center gap-1">
              <input
                data-testid="playlist-create-input"
                autoFocus
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="e.g., Rainy Days"
                className="px-3 py-1.5 rounded-full bg-white/5 border border-amber-400/40 text-white text-xs w-40 placeholder-slate-500 focus:outline-none"
              />
              <button type="submit" data-testid="playlist-create-save" className="p-1.5 text-emerald-400"><Check size={13} /></button>
              <button type="button" onClick={() => { setCreating(false); setNewName(""); }} className="p-1.5 text-slate-500"><X size={13} /></button>
            </form>
          ) : (
            <button
              data-testid="playlist-create-btn"
              onClick={() => setCreating(true)}
              className="flex items-center gap-1 px-3.5 py-1.5 rounded-full border border-dashed border-purple-500/30 text-slate-500 hover:text-amber-300 hover:border-amber-400/40 text-xs font-medium transition-colors"
            >
              <Plus size={12} /> New Playlist
            </button>
          )}
        </div>
        {plError && <p className="text-xs text-red-400 -mt-4 mb-6">{plError}</p>}

        {!selected || stories.length === 0 ? (
          <div data-testid="playlist-empty" className="glass-card rounded-2xl p-16 text-center">
            <ListMusic size={36} className="text-purple-400 mx-auto mb-5" />
            <h3 className="font-serif text-xl font-semibold text-white mb-2">
              {selected ? `"${selected.name}" is empty` : "No playlists yet"}
            </h3>
            <p className="text-sm text-slate-500 mb-6">Add stories from your library to queue up a listen.</p>
            <Link to="/my-stories" className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-gradient-to-r from-purple-600 to-amber-500 text-white text-sm font-semibold">
              <Sparkles size={14} /> Open Library
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            {/* Player */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="lg:col-span-3">
              <div data-testid="playlist-player" className="glass-card rounded-2xl overflow-hidden">
                <div className="h-56 relative">
                  {current?.cover_index != null ? (
                    <img src={`${API}/stories/${current.story_id}/illustrations/${current.cover_index}`} alt={current.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center" style={{ background: "linear-gradient(135deg, rgba(109,40,217,0.25), rgba(245,158,11,0.15))" }}>
                      <Moon size={40} className="text-amber-300/60" />
                    </div>
                  )}
                  <div className="absolute bottom-0 left-0 right-0 p-5 bg-gradient-to-t from-black/80 to-transparent">
                    <p className="text-xs font-mono uppercase tracking-wider text-amber-300">{selected.name} — Story {currentIdx + 1} of {stories.length}</p>
                    <h3 data-testid="playlist-current-title" className="font-serif text-xl font-bold text-white mt-1">{current?.title}</h3>
                  </div>
                </div>
                <div className="p-6">
                  {/* Voice */}
                  <div className="flex flex-wrap gap-2 mb-5">
                    {VOICES.map((v) => (
                      <div
                        key={v.id}
                        role="button"
                        tabIndex={0}
                        onClick={() => {
                          setVoice(v.id);
                          if (phase !== "idle" && phase !== "complete") loadStory(currentIdx, isPlaying, v.id);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            setVoice(v.id);
                            if (phase !== "idle" && phase !== "complete") loadStory(currentIdx, isPlaying, v.id);
                          }
                        }}
                        className={`flex items-center gap-1.5 pl-3.5 pr-1.5 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer ${
                          voice === v.id ? "bg-amber-500/20 border border-amber-400/40 text-amber-300" : "border border-purple-500/20 text-slate-400 hover:text-white"
                        }`}
                      >
                        {v.name}
                        <button
                          type="button"
                          data-testid={`voice-preview-button-${v.id}`}
                          onClick={(e) => handlePreview(e, v.id)}
                          title={`Hear a sample of ${v.name}`}
                          aria-label={`Preview ${v.name}'s voice`}
                          className={`w-6 h-6 rounded-full flex items-center justify-center transition-colors ${
                            previewPlayingId === v.id ? "text-amber-300" : "text-slate-500 hover:text-amber-300"
                          }`}
                        >
                          {previewLoadingId === v.id ? (
                            <Loader2 size={11} className="animate-spin" />
                          ) : previewPlayingId === v.id ? (
                            <Pause size={10} />
                          ) : (
                            <Ear size={12} />
                          )}
                        </button>
                      </div>
                    ))}
                  </div>
                  {previewError && (
                    <p data-testid="voice-preview-error" className="text-xs text-red-400 -mt-3 mb-4">{previewError}</p>
                  )}
                  {previewAudioId && (
                    <audio
                      ref={previewAudioRef}
                      src={`${API}/audio/${previewAudioId}`}
                      onPlay={() => { audioRef.current?.pause(); setPreviewPlayingId(previewVoiceId); }}
                      onPause={() => setPreviewPlayingId(null)}
                      onEnded={() => setPreviewPlayingId(null)}
                    />
                  )}

                  {/* Bedtime Countdown */}
                  <div className="flex flex-wrap items-center gap-2 mb-5">
                    <span className="flex items-center gap-1.5 text-xs text-slate-400 mr-1">
                      <Moon size={13} className="text-purple-400" /> Bedtime countdown
                    </span>
                    {BEDTIME_OPTIONS.map((n) => (
                      <button
                        key={n === null ? "none" : n}
                        type="button"
                        data-testid={`bedtime-countdown-${n === null ? "none" : n}`}
                        onClick={() => { setBedtimeLimit(n); setStoriesRemaining(n); }}
                        className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                          bedtimeLimit === n
                            ? "bg-purple-600/40 border border-purple-400/50 text-purple-200"
                            : "border border-purple-500/20 text-slate-500 hover:text-white"
                        }`}
                      >
                        {n === null ? "No limit" : `${n} ${n === 1 ? "story" : "stories"}`}
                      </button>
                    ))}
                    {bedtimeLimit != null && phase === "ready" && (
                      <span data-testid="bedtime-countdown-remaining" className="text-xs text-amber-300 font-mono ml-1">
                        {storiesRemaining} {storiesRemaining === 1 ? "story" : "stories"} left
                      </span>
                    )}
                  </div>

                  {phase === "idle" && (
                    <button
                      data-testid="playlist-start-btn"
                      onClick={() => { if (bedtimeLimit != null) setStoriesRemaining(bedtimeLimit); loadStory(0, true); }}
                      className="w-full py-3.5 rounded-full bg-gradient-to-r from-purple-600 to-amber-500 text-white font-semibold text-sm flex items-center justify-center gap-2 hover:shadow-xl hover:shadow-purple-500/30 transition-all"
                    >
                      <Play size={15} /> Play "{selected.name}"
                    </button>
                  )}
                  {phase === "loading" && (
                    <div className="flex items-center justify-center gap-3 py-3 text-sm text-slate-400">
                      <Loader2 size={16} className="animate-spin text-amber-400" />
                      Preparing "{current?.title}"...
                    </div>
                  )}
                  {phase === "error" && (
                    <div className="text-center py-2">
                      <p className="text-sm text-red-400 mb-3">{error}</p>
                      <button onClick={() => loadStory(currentIdx, true)} className="px-5 py-2 rounded-full border border-purple-500/30 text-slate-300 text-xs">Try Again</button>
                    </div>
                  )}
                  {phase === "lightsout" && (
                    <div data-testid="bedtime-lightsout" className="text-center py-4">
                      <Moon size={22} className="text-purple-300 mx-auto mb-3" />
                      <p className="font-serif text-lg text-white">Lights out — sweet dreams 🌙</p>
                      <p className="text-xs text-slate-500 mt-1">Your bedtime countdown finished after "{current?.title}".</p>
                      <button
                        data-testid="bedtime-one-more-btn"
                        onClick={() => {
                          setBedtimeLimit(1);
                          setStoriesRemaining(1);
                          if (currentIdx + 1 < stories.length) loadStory(currentIdx + 1, true);
                          else loadStory(0, true);
                        }}
                        className="mt-4 px-5 py-2 rounded-full border border-purple-500/30 text-slate-300 text-xs"
                      >
                        One more story
                      </button>
                    </div>
                  )}
                  {phase === "complete" && (
                    <div className="text-center py-4">
                      <Sparkles size={22} className="text-amber-400 mx-auto mb-3" />
                      <p className="font-serif text-lg text-white">Playlist complete — sweet dreams ✦</p>
                      <button onClick={() => { if (bedtimeLimit != null) setStoriesRemaining(bedtimeLimit); loadStory(0, true); }} className="mt-4 px-5 py-2 rounded-full border border-purple-500/30 text-slate-300 text-xs">Play Again</button>
                    </div>
                  )}
                  {phase === "ready" && audioId && (
                    <div>
                      <audio
                        key={audioId}
                        ref={audioRef}
                        src={`${API}/audio/${audioId}`}
                        autoPlay={autoplayRef.current}
                        onTimeUpdate={(e) => {
                          const t = e.target.currentTime;
                          const d = e.target.duration || 0;
                          setCurrentTime(t);
                          setProgress(d ? (t / d) * 100 : 0);
                          updateMediaSessionPosition(d, t);
                          if (d && t > 5 && t < d - 5) localStorage.setItem(`dw-resume-${audioId}`, String(t));
                        }}
                        onLoadedMetadata={(e) => {
                          setDuration(e.target.duration);
                          const saved = parseFloat(localStorage.getItem(`dw-resume-${audioId}`) || "0");
                          if (saved > 5 && saved < e.target.duration - 10) e.target.currentTime = saved;
                        }}
                        onPlay={() => setIsPlaying(true)}
                        onPause={() => setIsPlaying(false)}
                        onEnded={handleEnded}
                      />
                      <div className="flex items-center justify-center gap-5 mb-4">
                        <button
                          data-testid="playlist-prev-btn"
                          onClick={() => currentIdx > 0 && loadStory(currentIdx - 1, true)}
                          disabled={currentIdx === 0}
                          className="text-slate-400 hover:text-white disabled:opacity-30 transition-colors"
                        >
                          <SkipBack size={20} />
                        </button>
                        <button
                          data-testid="playlist-play-btn"
                          onClick={() => { const a = audioRef.current; if (a) { if (isPlaying) { a.pause(); } else { previewAudioRef.current?.pause(); a.play(); } } }}
                          className="w-14 h-14 rounded-full bg-gradient-to-r from-purple-600 to-amber-500 flex items-center justify-center text-white shadow-lg shadow-purple-500/30"
                        >
                          {isPlaying ? <Pause size={20} /> : <Play size={20} className="ml-0.5" />}
                        </button>
                        <button
                          data-testid="playlist-next-btn"
                          onClick={() => currentIdx + 1 < stories.length && loadStory(currentIdx + 1, true)}
                          disabled={currentIdx + 1 >= stories.length}
                          className="text-slate-400 hover:text-white disabled:opacity-30 transition-colors"
                        >
                          <SkipForward size={20} />
                        </button>
                      </div>

                      {/* Sentinel: mini-player visibility tracks this anchor
                          (right after transport controls) instead of the
                          whole tall player card, so it reliably clears the
                          viewport even on short pages/playlists. */}
                      <div ref={setPlayerCardRef} className="h-px" />

                      <input
                        type="range" min={0} max={100} step={0.1}
                        value={progress}
                        onChange={(e) => {
                          const a = audioRef.current;
                          if (a && a.duration) a.currentTime = (parseFloat(e.target.value) / 100) * a.duration;
                        }}
                        className="audio-progress h-2 w-full"
                        style={{ background: `linear-gradient(to right, #FBBF24 ${progress}%, rgba(255,255,255,0.15) 0%)` }}
                      />
                      <div className="flex justify-between text-xs text-slate-400 mt-1">
                        <span>{formatTime(currentTime)}</span>
                        <span>{formatTime(duration)}</span>
                      </div>
                      <SleepTimer audioRef={audioRef} />
                      <AmbientSounds active={isPlaying} />
                    </div>
                  )}
                </div>
              </div>
            </motion.div>

            {/* Queue */}
            <div className="lg:col-span-2">
              <div data-testid="playlist-queue" className="glass-card rounded-2xl p-5 space-y-2">
                <p className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-3">Up Next</p>
                {stories.map((s, i) => (
                  <div
                    key={s.story_id}
                    data-testid={`playlist-item-${s.story_id}`}
                    className={`flex items-center gap-3 p-3 rounded-xl transition-colors ${
                      i === currentIdx ? "bg-amber-500/10 border border-amber-400/30" : "hover:bg-white/5 border border-transparent"
                    }`}
                  >
                    <button
                      onClick={() => loadStory(i, true)}
                      className="flex-1 text-left min-w-0"
                    >
                      <p className={`text-sm font-medium truncate ${i === currentIdx ? "text-amber-300" : "text-white"}`}>{s.title}</p>
                      <p className="text-xs text-slate-500">
                        For {s.child_name}{s.sibling_name ? ` & ${s.sibling_name}` : ""}{s.word_count ? ` · ~${Math.ceil(s.word_count / 130)} min` : ""}
                      </p>
                    </button>
                    <div className="flex items-center gap-0.5 shrink-0">
                      <button onClick={() => moveStory(i, -1)} disabled={i === 0} className="p-1 text-slate-600 hover:text-white disabled:opacity-30"><ArrowUp size={13} /></button>
                      <button onClick={() => moveStory(i, 1)} disabled={i === stories.length - 1} className="p-1 text-slate-600 hover:text-white disabled:opacity-30"><ArrowDown size={13} /></button>
                      <button data-testid={`playlist-remove-${s.story_id}`} onClick={() => removeFromQueue(s.story_id)} className="p-1 text-slate-600 hover:text-red-400"><X size={13} /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
    <AnimatePresence>
      {(phase === "ready" || phase === "loading") && !playerInView && (
        <MiniPlayerBar
          title={current?.title || "Now Playing"}
          subtitle={phase === "loading" ? "Loading next story..." : `${formatTime(currentTime)} / ${formatTime(duration)}`}
          isPlaying={isPlaying}
          isBusy={phase === "loading"}
          progress={progress}
          onPlayPause={() => { const a = audioRef.current; if (a) { if (isPlaying) { a.pause(); } else { previewAudioRef.current?.pause(); a.play(); } } }}
          onSkipBack={() => currentIdx > 0 && loadStory(currentIdx - 1, true)}
          onSkipForward={() => currentIdx + 1 < stories.length && loadStory(currentIdx + 1, true)}
          onExpand={() => playerCardRef.current?.scrollIntoView({ behavior: "smooth", block: "center" })}
        />
      )}
    </AnimatePresence>
    </>
  );
}
